/**
 * Le stock d'un article à une date, et sa valeur au CMUP.
 *
 * *Décision de Chris, 07/09/2026 : « lorsqu'on interroge les qtés restantes à une date donnée on
 * a un stock final en qté puis en valorisation », valorisée au **coût moyen unitaire pondéré**.*
 *
 * ## Pourquoi un module pur, et pourquoi il ne touche pas la base
 *
 * La même raison qu'`etat-circuits` : c'est la seule chose ici qui puisse se tromper, et une
 * erreur y serait **invisible**. Un stock faux ne lève aucune exception, il affiche un nombre —
 * et personne ne recompte un magasin pour vérifier un écran. Une fonction pure — des mouvements
 * entrent, un état sort — s'éprouve sans base ni serveur, sur tous les enchaînements d'un coup.
 *
 * L'autre raison est qu'il n'y a **qu'une** implémentation. Le serveur s'en sert pour l'écran du
 * magasinier et pour ce que la contre-étude affichera quand on cliquera « du groupe » sur un
 * article. Deux calculs du même stock finiraient par ne plus dire le même chiffre.
 *
 * ## Le CMUP ne se calcule pas d'une moyenne, il se déroule
 *
 * C'est le point à comprendre avant de toucher à ce fichier. Le coût moyen pondéré **n'est pas**
 * la moyenne des coûts d'entrée pondérée par leurs quantités : il se recalcule à chaque entrée,
 * sur le stock présent *à cet instant-là*, lequel dépend des sorties déjà passées.
 *
 *     après une entrée   CMUP = (Q × CMUP + q × c) / (Q + q)
 *     après une sortie   CMUP inchangé, la sortie consomme au CMUP du moment
 *
 * Deux entrées identiques n'ont donc pas le même effet selon ce qui est sorti entre les deux, et
 * **l'ordre des mouvements fait partie du résultat**. C'est pour ça que la fonction déroule la
 * suite au lieu d'agréger : aucune moyenne calculée d'un seul trait ne donnerait le même nombre.
 *
 * ## Ce qui arrive quand le stock tombe à zéro, et quand il passe en dessous
 *
 * À zéro, le CMUP n'a plus de sens — il n'y a plus rien dont ce soit le coût. Il est **remis à
 * zéro** plutôt que conservé : garder l'ancien ferait réapparaître un coût d'avant sur la
 * prochaine entrée, mélangé à elle, alors que plus aucune unité de cette époque n'est là.
 *
 * En dessous de zéro, on ne devine pas. Une sortie plus grande que le stock est **acceptée et
 * signalée** — même règle que le dépassement d'une livraison, tranchée le 03/09/2026 : le
 * magasin a réellement sorti la marchandise, le refuser ferait mentir l'écran sur ce qui est
 * dans le rayonnage. La quantité devient négative, la valeur suit au dernier CMUP connu, et
 * `negatif` le dit pour que l'écran puisse le montrer.
 */
import { dec } from "./nombre.js";
const ZERO = dec(0);
/** Les décimales du schéma : `numeric(14,3)` pour les quantités, `numeric(14,5)` pour les coûts. */
const DECIMALES_QUANTITE = 3;
const DECIMALES_COUT = 5;
const DECIMALES_VALEUR = 2;
/**
 * L'ordre dans lequel les mouvements se déroulent.
 *
 * Recalculé ici plutôt que confié à l'appelant : une règle qui dépendrait du `ORDER BY` d'une
 * requête se tromperait le jour où quelqu'un lit les mêmes lignes autrement, et rien ne le
 * signalerait — le stock serait simplement faux d'un montant que personne ne saurait expliquer.
 *
 * À date égale, l'ordre du tableau reçu tranche (`Array.prototype.sort` est stable depuis ES2019).
 * Deux mouvements du même instant ne s'ordonnent par rien d'autre, et c'est assumé : ils sont
 * commutatifs sur la quantité, et leur effet sur le CMUP à la cinquième décimale ne vaut pas
 * qu'on invente un départage.
 */
function enOrdre(mouvements) {
    return [...mouvements].sort((a, b) => (a.survenuLe < b.survenuLe ? -1 : a.survenuLe > b.survenuLe ? 1 : 0));
}
/**
 * Déroule les mouvements et rend l'état du stock.
 *
 * `jusquAu` coupe la suite : tout mouvement postérieur est ignoré, ce qui est exactement
 * l'interrogation « où en était le stock à cette date ». Omis, tout est pris.
 *
 * La date de coupure se compare en **chaînes ISO**, jamais par un objet date : c'est la
 * convention du calendrier du produit, et c'est ce qui rend impossible le décalage de fuseau qui
 * avait fait compter 254 jours ouvrés au lieu de 252 sur bien-archiver.
 */
export function etatDuStock(mouvements, jusquAu) {
    let quantite = ZERO;
    let cmup = ZERO;
    let comptes = 0;
    let negatif = false;
    for (const mouvement of enOrdre(mouvements)) {
        if (jusquAu !== undefined && mouvement.survenuLe > jusquAu)
            continue;
        comptes += 1;
        const q = dec(mouvement.quantite);
        if (mouvement.sens === "entree") {
            const c = dec(mouvement.coutUnitaire);
            const apres = quantite.plus(q);
            // Le CMUP ne se recalcule que sur du stock POSITIF. Quand on entre sur un stock négatif,
            // pondérer par une quantité négative donnerait un coût moyen aberrant — parfois négatif.
            // L'entrée impose alors son propre coût : c'est la seule marchandise réellement présente.
            cmup = quantite.lessThanOrEqualTo(ZERO) || apres.lessThanOrEqualTo(ZERO)
                ? c
                : quantite.times(cmup).plus(q.times(c)).dividedBy(apres);
            quantite = apres;
        }
        else {
            // Une sortie consomme au CMUP du moment : elle ne le change pas.
            quantite = quantite.minus(q);
        }
        if (quantite.isNegative())
            negatif = true;
        // Plus rien en stock : le coût moyen n'a plus d'objet. Le garder ferait resurgir un prix
        // d'avant, mélangé à la prochaine entrée, alors qu'aucune unité de cette époque n'est là.
        if (quantite.isZero())
            cmup = ZERO;
    }
    const valeur = quantite.times(cmup);
    return {
        quantite: quantite.toFixed(DECIMALES_QUANTITE),
        cmup: cmup.toFixed(DECIMALES_COUT),
        valeur: valeur.toFixed(DECIMALES_VALEUR),
        mouvements: comptes,
        negatif,
    };
}
/**
 * Déroule la suite et rend, pour CHAQUE mouvement, ce qu'il a réellement pesé.
 *
 * **Le tableau rendu suit l'ordre REÇU**, pas l'ordre chronologique — alors que le calcul, lui,
 * se fait dans l'ordre chronologique. C'est ce qui permet à l'appelant d'écrire `valorises[i]`
 * en face de `mouvements[i]` sans rien apparier.
 *
 * La première version rendait l'ordre chronologique, à charge pour l'appelant de retrouver
 * l'appariement par une clé — et deux mouvements du même instant, de même sens et de même
 * quantité, ce qui arrive dès qu'on saisit deux fois la même livraison, se seraient confondus.
 */
export function valoriserLesMouvements(mouvements) {
    // L'index d'origine voyage avec le mouvement : c'est lui qui remet chaque résultat à sa place.
    const indexes = mouvements.map((mouvement, index) => ({ mouvement, index }));
    indexes.sort((a, b) => a.mouvement.survenuLe < b.mouvement.survenuLe
        ? -1
        : a.mouvement.survenuLe > b.mouvement.survenuLe
            ? 1
            : 0);
    const valorises = new Array(mouvements.length);
    let quantite = ZERO;
    let cmup = ZERO;
    for (const { mouvement, index } of indexes) {
        const q = dec(mouvement.quantite);
        let coutRetenu;
        if (mouvement.sens === "entree") {
            coutRetenu = dec(mouvement.coutUnitaire);
            const apres = quantite.plus(q);
            cmup =
                quantite.lessThanOrEqualTo(ZERO) || apres.lessThanOrEqualTo(ZERO)
                    ? coutRetenu
                    : quantite.times(cmup).plus(q.times(coutRetenu)).dividedBy(apres);
            quantite = apres;
        }
        else {
            // Le CMUP D'AVANT la sortie : c'est le coût des unités qui partent. Le lire après serait
            // identique — une sortie ne change pas le coût moyen — mais l'écrire ainsi dit pourquoi.
            coutRetenu = cmup;
            quantite = quantite.minus(q);
        }
        if (quantite.isZero())
            cmup = ZERO;
        valorises[index] = {
            coutRetenu: coutRetenu.toFixed(DECIMALES_COUT),
            valeur: q.times(coutRetenu).toFixed(DECIMALES_VALEUR),
            quantiteApres: quantite.toFixed(DECIMALES_QUANTITE),
            cmupApres: cmup.toFixed(DECIMALES_COUT),
        };
    }
    return valorises;
}
/**
 * La quantité seule, sans la valorisation.
 *
 * C'est ce que `article.stock_magasin` doit porter après chaque mouvement — et le fait de le
 * dériver d'ici, plutôt que d'incrémenter un compteur, est ce qui permet de corriger un
 * mouvement saisi de travers sans que la colonne parte à la dérive.
 */
export function quantiteEnStock(mouvements, jusquAu) {
    return etatDuStock(mouvements, jusquAu).quantite;
}
/**
 * Où se trouve le stock d'un article, site par site.
 *
 * *Demande de Chris, 15/09/2026 : plusieurs sites de stockage, et « quand l'assistante est dans la
 * contre étude elle doit pouvoir choisir de voir les stock pour un magasin ou pour tous ».*
 *
 * ## Un SEUL coût moyen, celui de l'article
 *
 * *Tranché par Chris le 15/09/2026, entre les deux valorisations possibles.* Le CMUP se déroule
 * sur tous les mouvements de l'article, quel que soit le magasin où la marchandise est posée, et
 * la valeur d'un site est sa quantité multipliée par ce coût-là.
 *
 * Deux propriétés en découlent, et ce sont elles qui ont fait choisir ainsi :
 *
 *  - **les valeurs des sites s'additionnent** pour donner la valeur totale, aux arrondis près —
 *    un CMUP par site aurait donné un total qui ne se divise plus par la quantité totale ;
 *  - **déplacer une palette d'un magasin à l'autre ne crée ni ne détruit de valeur**, alors qu'un
 *    coût moyen par site l'aurait fait sortir à un prix et rentrer à un autre.
 *
 * ## Un site qui a tout sorti reste affiché
 *
 * Il figure avec une quantité nulle plutôt que de disparaître. C'est un fait — de la marchandise
 * y est passée —, et le faire disparaître ferait lire « cet article n'a jamais été ici », ce qui
 * est une autre affirmation. La liste ne contient donc que des sites qui ont bougé : les autres
 * n'y sont pas, et c'est à l'écran de dire « aucun mouvement ».
 *
 * ## L'ordre est celui des identifiants, pas celui des mouvements
 *
 * Un ordre tiré de l'ordre d'arrivée dépendrait du `ORDER BY` de l'appelant, donc changerait sans
 * raison visible d'un écran à l'autre. Les noms de sites ne sont pas connus ici : c'est à
 * l'appelant de reclasser sur ce qu'il affiche. Le « site non désigné » passe en dernier — il ne
 * répond à rien, il signale seulement ce qui reste à réparer.
 */
export function repartitionParSite(mouvements, jusquAu) {
    const total = etatDuStock(mouvements, jusquAu);
    const cmup = dec(total.cmup);
    const quantites = new Map();
    for (const mouvement of mouvements) {
        if (jusquAu !== undefined && mouvement.survenuLe > jusquAu)
            continue;
        const site = mouvement.siteId ?? null;
        const q = dec(mouvement.quantite);
        const avant = quantites.get(site) ?? ZERO;
        quantites.set(site, mouvement.sens === "entree" ? avant.plus(q) : avant.minus(q));
    }
    const sites = [...quantites.entries()]
        .sort(([a], [b]) => {
        if (a === b)
            return 0;
        // Le site non désigné en dernier, quoi qu'il arrive.
        if (a === null)
            return 1;
        if (b === null)
            return -1;
        return a < b ? -1 : 1;
    })
        .map(([siteId, quantite]) => ({
        siteId,
        quantite: quantite.toFixed(DECIMALES_QUANTITE),
        valeur: quantite.times(cmup).toFixed(DECIMALES_VALEUR),
    }));
    return { sites, total };
}
//# sourceMappingURL=stock.js.map